import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslate, useApiUrl } from "@pankod/refine-core";
import {
  Form,
  Select,
  InputNumber,
  Switch,
  Alert,
} from "@pankod/refine-antd";
import { Spin } from "antd";
import axios from "axios";

import KeywordTagsSelect from "../keywordTagsSelect";

interface Props {
  fieldName: number; // antd Form.List field.name (index into the levels array)
}

interface QuizOption {
  label: string;
  value: string;
}

/**
 * Trivia level config. Two modes via the local "Unique trivia" switch:
 *
 *  - ON  → pin a specific quiz via `config.trivia_id`. The picker is a
 *          free-text search-as-you-type Select that queries the
 *          /api/v2/quiz?q=... endpoint (same pattern as KeywordTagsSelect),
 *          since there can be thousands of quizzes — preloading them all
 *          isn't viable. Search fires once the user has typed 3+ characters
 *          (debounced 300ms). difficulty + tags hidden and cleared from form
 *          state on toggle.
 *  - OFF → `config.difficulty` + `config.tags` (KeywordTagsSelect, strict
 *          mode — only existing tags from /quiz-keyword). `config.trivia_id`
 *          cleared on toggle.
 *
 * The switch is NOT a form field — it lives in component state so it never
 * leaks into the submitted payload. Initial mode is inferred at mount from
 * whether the saved config already has a trivia_id.
 *
 * On edit hydration, we one-shot-fetch /api/v2/quiz/:id to get the saved
 * quiz's `question` so the Select can render its label (otherwise antd
 * would render the raw UUID).
 *
 * If the backend returned `trivia_exists: false` for this level (the pinned
 * quiz was deleted), we render a warning Alert inside the unique-mode view.
 */
export const TriviaLevelConfig: React.FC<Props> = ({ fieldName }) => {
  const t = useTranslate();
  const form = Form.useFormInstance();
  const apiUrl = useApiUrl();
  const APIToken =
    typeof window !== "undefined" ? localStorage.getItem("api-token") : null;

  // Read the current saved trivia_id so the quiz Select can render its label
  // even if the quiz isn't in the current search results.
  const currentTriviaId: string | undefined = Form.useWatch(
    ["thematic", "levels", fieldName, "config", "trivia_id"]
  );
  // Admin-only stale-reference flag from backend.
  const triviaExists: boolean | undefined = Form.useWatch(
    ["thematic", "levels", fieldName, "trivia_exists"]
  );

  // Infer initial unique mode once from the form's current state.
  const [unique, setUnique] = useState<boolean>(() => {
    const v = form.getFieldValue([
      "thematic",
      "levels",
      fieldName,
      "config",
      "trivia_id",
    ]);
    return !!v;
  });

  // Re-sync once if hydration arrives after mount (edit page: queryResult
  // resolves after the form is first rendered). After that, the user's
  // toggle wins.
  const syncedRef = useRef(false);
  useEffect(() => {
    if (syncedRef.current) return;
    if (currentTriviaId) {
      setUnique(true);
      syncedRef.current = true;
    }
  }, [currentTriviaId]);

  // ── Quiz search ────────────────────────────────────────────────────────
  const [quizOptions, setQuizOptions] = useState<QuizOption[]>([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQuizSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (!value || value.length < 3) {
        setQuizOptions([]);
        setQuizLoading(false);
        return;
      }
      setQuizLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        axios
          .get(`${apiUrl}/quiz`, {
            // Prefix `%` so the backend's LIKE-style match scans anywhere in
            // the question text instead of anchoring to the start. axios
            // would normally URL-encode `%` to `%25`; the paramsSerializer
            // below preserves it literally while still encoding other
            // special chars (spaces, &, etc.).
            params: { q: `%${value}` },
            paramsSerializer: (p) =>
              Object.entries(p)
                .map(
                  ([k, v]) =>
                    `${k}=${encodeURIComponent(String(v)).replace(/%25/g, "%")}`
                )
                .join("&"),
            headers: { Authorization: `Bearer ${APIToken}` },
          })
          .then((res) => {
            const items: any[] = Array.isArray(res.data) ? res.data : [];
            setQuizOptions(
              items.map((q) => ({
                label: q.question ?? q.quiz_id,
                value: q.quiz_id,
              }))
            );
          })
          .catch(() => setQuizOptions([]))
          .finally(() => setQuizLoading(false));
      }, 300);
    },
    [apiUrl, APIToken]
  );

  // One-shot fetch of the currently-pinned quiz so its question can render
  // as the Select's label on edit hydration. Without this, antd would show
  // the raw UUID. Re-runs only when the pinned id changes.
  const [savedQuizLabel, setSavedQuizLabel] = useState<string>("");
  useEffect(() => {
    if (!currentTriviaId) {
      setSavedQuizLabel("");
      return;
    }
    axios
      .get(`${apiUrl}/quiz/${currentTriviaId}`, {
        headers: { Authorization: `Bearer ${APIToken}` },
      })
      .then((res) => setSavedQuizLabel(res.data?.question ?? ""))
      .catch(() => setSavedQuizLabel(""));
  }, [currentTriviaId, apiUrl, APIToken]);

  // Merge the saved quiz's label into options (if not already present from
  // search results) so the Select displays "question" instead of the UUID.
  const mergedOptions: QuizOption[] =
    currentTriviaId &&
    savedQuizLabel &&
    !quizOptions.some((o) => o.value === currentTriviaId)
      ? [{ label: savedQuizLabel, value: currentTriviaId }, ...quizOptions]
      : quizOptions;

  // ── Mode toggle ────────────────────────────────────────────────────────
  const onToggleUnique = (checked: boolean) => {
    setUnique(checked);
    if (checked) {
      form.setFields([
        {
          name: ["thematic", "levels", fieldName, "config", "level"],
          value: undefined,
        },
        {
          name: ["thematic", "levels", fieldName, "config", "keywords"],
          value: undefined,
        },
      ]);
    } else {
      form.setFields([
        {
          name: ["thematic", "levels", fieldName, "config", "trivia_id"],
          value: undefined,
        },
      ]);
    }
  };

  return (
    <>
      <Form.Item label={t("event.thematic.unique_trivia")}>
        <Switch checked={unique} onChange={onToggleUnique} />
      </Form.Item>

      {unique ? (
        <>
          {triviaExists === false && (
            <Alert
              type="warning"
              showIcon
              message={t("event.thematic.trivia_stale_warning")}
              style={{ marginBottom: 12 }}
            />
          )}
          <Form.Item
            label={t("event.thematic.trivia_pick")}
            name={[fieldName, "config", "trivia_id"]}
            rules={[{ required: true, message: t("event.thematic.trivia_pick") }]}
            extra={t("event.thematic.trivia_search_hint")}
          >
            <Select
              showSearch
              filterOption={false}
              onSearch={handleQuizSearch}
              options={mergedOptions}
              loading={quizLoading}
              notFoundContent={quizLoading ? <Spin size="small" /> : null}
              placeholder={t("event.thematic.trivia_search_placeholder")}
              allowClear
            />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item
            label={t("event.thematic.trivia_level")}
            name={[fieldName, "config", "level"]}
          >
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item
            label={t("event.thematic.trivia_keywords")}
            name={[fieldName, "config", "keywords"]}
            extra={t("event.thematic.trivia_keywords_hint")}
          >
            {/* strict: thematic trivia levels can only reference existing
                quiz-keyword entries (no free-form creation), unlike the quiz
                keywords field which uses this component without `strict`. */}
            <KeywordTagsSelect
              strict
              placeholder={t("event.thematic.trivia_keywords_placeholder")}
            />
          </Form.Item>
        </>
      )}
    </>
  );
};
